import React, { useEffect } from 'react'
import { FaHistory } from 'react-icons/fa'
import { Box, Button, Icon, Stack, Text, Badge, Loading, SimpleTable, SimplePopover } from '@/components/lib'
import ResponseValueCell from './ResponseValueCell'
import utils from '@/utils/utils'

// The history of a response — every submission that built it, with each
// field's old and new value — behind a popover that only fetches when it is
// opened. The viewer keeps one copy of the history per response and hands it
// to every popover on that response (the whole-response one in the header and
// the per-field one on each row), so opening several rows costs one request.

function who (submission) {
  if (submission.createdByType === 'participant') return 'Participant'
  const u = submission.createdByUser
  if (!u) return 'Team member'
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email
}

function cellValue (dataType, value) {
  if (value === null || value === undefined || value === '') return <Text color='fg.subtle'>—</Text>
  let v = value
  if (dataType?.type === 'choice' && (typeof v === 'string' || v instanceof String)) v = v.split(',')
  if (!dataType) return <Text>{String(v)}</Text>
  return <ResponseValueCell dataType={dataType} value={v} />
}

function Change ({ field, dataType, showName }) {
  return (
    <Stack direction='row' gap={2} alignItems='baseline' flexWrap='wrap'>
      {showName && <Text fontSize='sm' fontWeight='bold'>{dataType ? (dataType.nickname || dataType.name) : 'Field'}:</Text>}
      {field.changed
        ? (
          <>
            <Box textDecoration='line-through' color='fg.muted'>{cellValue(dataType, field.previousValue)}</Box>
            <Text color='fg.muted'>→</Text>
          </>
          )
        : null}
      {cellValue(dataType, field.value)}
      {field.changed && <Badge colorPalette='orange' variant='outline'>changed</Badge>}
    </Stack>
  )
}

function HistoryBody ({ history, loading, onOpen, dataTypes, dataType }) {
  // Mounted only while the popover is open (SimplePopover `lazy`), so this is
  // the "fetch on click" the viewer relies on.
  useEffect(() => { onOpen() }, [onOpen])

  if (loading || !history) return <Loading />

  const byId = Object.fromEntries((dataTypes || []).map(dt => [dt._id, dt]))
  const submissions = dataType
    ? history.filter(s => s.fields.some(f => f.dataType === dataType._id))
    : history
  if (!submissions.length) return <Text color='fg.subtle'>No history has been recorded for this {dataType ? 'field' : 'response'}.</Text>

  // Newest first: the question is usually "what happened to this most recently".
  const rows = [...submissions].reverse().map(s => {
    const fields = dataType ? s.fields.filter(f => f.dataType === dataType._id) : s.fields
    return [
      <Box key='when' whiteSpace='nowrap'>
        <Text>{utils.formatDateTime(s.createdAt)}</Text>
        {s.partialSubmission && <Badge colorPalette='gray' variant='outline'>partial</Badge>}
      </Box>,
      <Box key='who'>
        <Text>{who(s)}</Text>
        {s.client?.ipAddress && <Text fontSize='xs' color='fg.subtle'>{s.client.ipAddress}</Text>}
      </Box>,
      <Text key='note' color={s.updateNote ? undefined : 'fg.subtle'}>{s.updateNote || '—'}</Text>,
      <Stack key='changes' gap={1}>
        {fields.map(f => <Change key={f.dataType} field={f} dataType={byId[f.dataType]} showName={!dataType} />)}
        {!dataType && s.disregarded?.length > 0 &&
          <Text fontSize='xs' color='fg.subtle'>
            Not recorded because its condition was no longer met: {s.disregarded.map(id => byId[id]?.nickname || byId[id]?.name || 'a field').join(', ')}
          </Text>}
      </Stack>
    ]
  })

  return (
    <Stack gap={2}>
      <Box overflowX='auto' maxH='400px' overflowY='auto'>
        <SimpleTable size='sm' headers={['When', 'Who', 'Note', dataType ? 'Value' : 'Changes']} rows={rows} />
      </Box>
    </Stack>
  )
}

export default function ResponseHistoryPopover ({ history, loading, onOpen, dataTypes, dataType, compact }) {
  const label = dataType ? 'History of this field' : 'History of this response'
  // The trigger must be the element PopoverTrigger clones its handlers onto,
  // so the compact icon carries its label as `title` rather than being
  // wrapped in a Tooltip that would swallow the click.
  const trigger = compact
    ? (
      <Button size='xs' variant='ghost' aria-label={label} title={label}>
        <Icon><FaHistory /></Icon>
      </Button>
      )
    : (
      <Button size='2xs' variant='ghost'>
        <Icon><FaHistory /></Icon> History
      </Button>
      )
  return (
    <SimplePopover
      lazy
      contentProps={{ w: dataType ? '560px' : '760px', maxW: '95vw' }}
      trigger={trigger}
      header={label}
      body={<HistoryBody history={history} loading={loading} onOpen={onOpen} dataTypes={dataTypes} dataType={dataType} />}
    />
  )
}
